import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { PixelIcon } from '@/components/ui/PixelIcon'
import { Tooltip, TooltipBody } from '@/components/ui/Tooltip'
import { POWERUPS, type PowerupTier } from '@/game/catalog'
import { useProgress } from '@/contexts/ProgressProvider'

interface PowerupDockProps {
  onActivate: (powerupId: string) => void
}

const TIER_COLOR: Record<PowerupTier, string> = {
  basic: 'var(--surface-sunken)',
  premium: '#3d2f63',
  legendary: '#5c4415',
}

const TIER_RING: Record<PowerupTier, string> = {
  basic: 'transparent',
  premium: '#a678ff44',
  legendary: '#ffc10744',
}

const TIER_LABEL: Record<PowerupTier, string> = {
  basic: 'Basic',
  premium: 'Premium',
  legendary: 'Legendary',
}

export function PowerupDock({ onActivate }: PowerupDockProps) {
  const { save } = useProgress()
  const prevCounts = useRef<Record<string, number> | null>(null)
  const [pulse, setPulse] = useState<{ id: string; seq: number } | null>(null)
  const pulseSeq = useRef(0)

  useEffect(() => {
    const prev = prevCounts.current
    prevCounts.current = { ...save.powerups }
    if (!prev) return
    for (const p of POWERUPS) {
      const before = prev[p.id] ?? 0
      const after = save.powerups[p.id] ?? 0
      if (after < before) {
        pulseSeq.current += 1
        setPulse({ id: p.id, seq: pulseSeq.current })
        break
      }
    }
  }, [save.powerups])

  return (
    <div className="flex flex-wrap items-center justify-center gap-1.5 px-3">
      {POWERUPS.map((p, i) => {
        const owned = save.powerups[p.id] ?? 0
        const hotkey = i === 9 ? '0' : String(i + 1)
        return (
          <Tooltip
            key={p.id}
            content={
              <TooltipBody
                title={p.name}
                lines={[p.description, `${TIER_LABEL[p.tier]} · key ${hotkey} · ${owned} owned`]}
              />
            }
          >
            <motion.button
              disabled={owned <= 0}
              onClick={() => onActivate(p.id)}
              whileTap={owned > 0 ? { scale: 0.88 } : undefined}
              aria-label={`${p.name} (${owned} owned, key ${hotkey})`}
              className="relative grid place-items-center w-11 h-11 rounded-[var(--r-md)] bg-[var(--surface-raised)] shadow-emboss text-[var(--text-dim)] disabled:opacity-40 disabled:pointer-events-none enabled:hover:text-[var(--text)] transition"
              style={{ boxShadow: `var(--shadow-emboss), 0 0 0 1.5px ${TIER_RING[p.tier]}` }}
            >
              <PixelIcon name={p.icon} size={19} />
              <AnimatePresence>
                {pulse?.id === p.id && (
                  <motion.span
                    key={pulse.seq}
                    className="pointer-events-none absolute inset-0 rounded-[var(--r-md)]"
                    style={{ border: '2px solid var(--ember)' }}
                    initial={{ opacity: 0.9, scale: 1 }}
                    animate={{ opacity: 0, scale: 1.7 }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                    onAnimationComplete={() => setPulse((cur) => (cur?.seq === pulse.seq ? null : cur))}
                  />
                )}
              </AnimatePresence>
              <span
                className="absolute -bottom-1 -right-1 text-[9px] font-black px-1 rounded-full"
                style={{ background: TIER_COLOR[p.tier] }}
              >
                {hotkey}
              </span>
              {owned > 0 && (
                <motion.span
                  key={`count-${owned}`}
                  initial={{ scale: 1.5 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                  className="absolute -top-1 -right-1 text-[9px] font-black px-1 rounded-full"
                  style={{ background: 'var(--ember)', color: '#231404' }}
                >
                  {owned}
                </motion.span>
              )}
            </motion.button>
          </Tooltip>
        )
      })}
    </div>
  )
}
